import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth, storage } from '../firebase';
import { onAuthStateChanged, deleteUser, signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getUserProfile, updateUserProfile, getCurrentProgram, clearAllScheduledActivities, setupProgramByPersona, deleteUserAccount } from '../utils/database';
import { useTheme } from '../contexts/ThemeContext';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import PaceSettingsModal from '../components/PaceSettingsModal';
import ProfileEditScreen from '../components/ProfileEditScreen';
import ClubSettingsSection from '../components/ClubSettingsSection';
import CoachSettingsSection from '../components/CoachSettingsSection';
import Badge from '../assets/images/Begin-Badge.png';
import UploadIcon from '../assets/icons/upload-cloud-01.svg';
import '../styles/Dashboard.css';
import '../styles/Settings.css';

function Settings() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { themePreference, initializeFromUserData } = useTheme();
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [devAction, setDevAction] = useState(''); // For dev section loading states
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const hasUserMadeChanges = useRef(false); // Only true after user interacts with inputs
  
  // Helper to calculate age from birthdate
  const calculateAge = (birthdateStr) => {
    if (!birthdateStr) return null;
    const today = new Date();
    const birth = new Date(birthdateStr);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: true,
    emailUpdates: true,
    timezone: 'America/Los_Angeles',
    units: 'imperial',
    weekStartsOn: 'sunday',
    distanceGoalType: 'weekly', // 'daily' or 'weekly'
    distanceGoal: 20, // miles/km per week (default: 20 miles/week = ~87 miles/month)
    durationGoalType: 'weekly', // 'daily' or 'weekly'
    durationGoal: 150, // minutes per week (default: 150 min/week = ~650 min/month)
  });
  
  // Pace settings state (for distance estimates)
  const [paceSettings, setPaceSettings] = useState({
    runPaceMinutes: 8,           // Run pace minutes part (e.g., 8 for 8:30)
    runPaceSeconds: 30,          // Run pace seconds part (e.g., 30 for 8:30)
    bikingAvgMph: 14,            // Average biking speed in mph
    swimPaceMinutes: 2,          // Swim pace per 100yd minutes part
    swimPaceSeconds: 30,         // Swim pace per 100yd seconds part
    walkPaceMinutes: 17,         // Walk pace per mile in minutes
  });
  
  // Profile state
  const [profile, setProfile] = useState({
    birthdate: '',
    age: '',
    weight: '',
    weightUnit: 'lb',
    goalWeight: '',
    goalWeightUnit: 'lb',
    height: '',
    heightUnit: 'in',
    gender: '',
    experienceLevel: '',
    personaId: '',
  });
  
  // Apple Health state
  const [appleHealthConnected, setAppleHealthConnected] = useState(false);
  const [appleHealthLastSync, setAppleHealthLastSync] = useState(null);
  
  // Profile picture state
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cropMaskSize = 300; // Fixed crop mask size (square)
  
  // Current program state
  const [, setCurrentProgram] = useState(null);
  
  
  // Navigation state (for settings screens)
  const [showPaceSettings, setShowPaceSettings] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  
  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        
        // Load profile picture preview
        if (userData.profilePictureUrl) {
          setProfilePicturePreview(userData.profilePictureUrl);
        } else {
          setProfilePicturePreview(null);
        }
        
        // Load settings
        if (userData.settings) {
          setSettings({
            notifications: userData.settings.notifications !== undefined ? userData.settings.notifications : true,
            emailUpdates: userData.settings.emailUpdates !== undefined ? userData.settings.emailUpdates : true,
            timezone: userData.settings.timezone || 'America/Los_Angeles',
            units: userData.settings.units || 'imperial',
            weekStartsOn: userData.settings.weekStartsOn || 'sunday',
            distanceGoalType: userData.settings.distanceGoalType || 'weekly',
            distanceGoal: userData.settings.distanceGoal || 20,
            durationGoalType: userData.settings.durationGoalType || 'weekly',
            durationGoal: userData.settings.durationGoal || 150,
          });
          
          // Initialize theme from user's saved preference
          if (userData.settings.themePreference) {
            initializeFromUserData(userData.settings.themePreference);
          }
        }
        
        // Load pace settings
        if (userData.paceSettings) {
          const runPaceSeconds = userData.paceSettings.runPaceSecondsPerMile || 510;
          const swimPace = userData.paceSettings.swimPacePer100 || 2.5;
          setPaceSettings({
            runPaceMinutes: Math.floor(runPaceSeconds / 60),
            runPaceSeconds: runPaceSeconds % 60,
            bikingAvgMph: userData.paceSettings.bikingAvgMph || 14,
            swimPaceMinutes: Math.floor(swimPace),
            swimPaceSeconds: Math.round((swimPace % 1) * 60),
            walkPaceMinutes: userData.paceSettings.walkPaceMinutesPerMile || 17,
          });
        }
        
        // Load profile data
        // Handle height: prefer height field, fallback to heightIn (convert from inches)
        let height = '';
        let heightUnit = 'in';
        if (userData.height !== undefined && userData.height !== null) {
          height = userData.height;
          heightUnit = userData.heightUnit || 'in';
        } else if (userData.heightIn !== undefined && userData.heightIn !== null) {
          // Convert from stored inches
          height = userData.heightIn;
          heightUnit = 'in';
        }
        
        setProfile({
          birthdate: userData.birthdate || '',
          age: userData.age || '',
          weight: userData.weight || '',
          weightUnit: userData.weightUnit || 'lb',
          goalWeight: userData.goalWeight || '',
          goalWeightUnit: userData.goalWeightUnit || 'lb',
          height: height,
          heightUnit: heightUnit,
          gender: userData.gender || '',
          experienceLevel: userData.experienceLevel || '',
          personaId: userData.personaId || '',
        });
        
        // Load Apple Health status
        setAppleHealthConnected(userData.appleHealthConnected || false);
        if (userData.appleHealthLastSync) {
          setAppleHealthLastSync(userData.appleHealthLastSync);
        }
        
        // Load current program
        try {
          const programData = await getCurrentProgram(userId);
          setCurrentProgram(programData);
        } catch (error) {
          console.error('Error loading current program:', error);
          setCurrentProgram(null);
        }
        
        setInitialLoadComplete(true);
        setLoading(false);
      } catch (error) {
        console.error('[Settings] Error loading user:', error);
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate, initializeFromUserData]);


  // Scroll to club/coach section when arriving from signup
  useEffect(() => {
    if (location.state?.highlightClub && initialLoadComplete) {
      document.getElementById('settings-club-section')?.scrollIntoView({ behavior: 'smooth' });
    }
    if (location.state?.highlightCoach && initialLoadComplete) {
      document.getElementById('settings-coach-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.state?.highlightClub, location.state?.highlightCoach, initialLoadComplete]);

  const handleSettingsChange = (key, value) => {
    hasUserMadeChanges.current = true;
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleProfileChange = (key, value) => {
    hasUserMadeChanges.current = true;
    setProfile(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSavePaceSettings = async (newPaceSettings) => {
    setSaving(true);
    setSuccessMessage('');
    
    // Convert pace inputs to storage format
    const runPaceSecondsPerMile = (newPaceSettings.runPaceMinutes * 60) + newPaceSettings.runPaceSeconds;
    const swimPacePer100 = newPaceSettings.swimPaceMinutes + (newPaceSettings.swimPaceSeconds / 60);
    
    try {
      await updateUserProfile(userId, {
        paceSettings: {
          runPaceSecondsPerMile: runPaceSecondsPerMile,
          runPaceDisplay: `${newPaceSettings.runPaceMinutes}:${String(newPaceSettings.runPaceSeconds).padStart(2, '0')}`,
          bikingAvgMph: newPaceSettings.bikingAvgMph,
          walkPaceMinutesPerMile: newPaceSettings.walkPaceMinutes,
          swimPacePer100: swimPacePer100,
          swimPaceUnit: 'yards',
        },
      });
      
      setPaceSettings(newPaceSettings);
      setSuccessMessage('Pace settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving pace settings:', error);
      setSuccessMessage('Error saving pace settings. Please try again.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };
  
  

  // eslint-disable-next-line no-unused-vars
  const handleSaveSettings = async () => {
    setSaving(true);
    setSuccessMessage('');
    
    // Convert pace inputs to storage format
    const runPaceSecondsPerMile = (paceSettings.runPaceMinutes * 60) + paceSettings.runPaceSeconds;
    const swimPacePer100 = paceSettings.swimPaceMinutes + (paceSettings.swimPaceSeconds / 60);
    
    try {
      // Upload profile picture if changed
      let profilePictureUrl = user?.profilePictureUrl || null;
      if (profilePictureFile) {
        const fileExtension = profilePictureFile.name.split('.').pop();
        const storageRef = ref(storage, `users/${userId}/profile-picture.${fileExtension}`);
        await uploadBytes(storageRef, profilePictureFile);
        profilePictureUrl = await getDownloadURL(storageRef);
        setProfilePictureFile(null);
      }
      
      await updateUserProfile(userId, {
        settings: settings,
        paceSettings: {
          runPaceSecondsPerMile: runPaceSecondsPerMile,
          runPaceDisplay: `${paceSettings.runPaceMinutes}:${String(paceSettings.runPaceSeconds).padStart(2, '0')}`,
          bikingAvgMph: paceSettings.bikingAvgMph,
          walkPaceMinutesPerMile: paceSettings.walkPaceMinutes,
          swimPacePer100: swimPacePer100,
          swimPaceUnit: 'yards',
        },
        birthdate: profile.birthdate || null,
        age: profile.birthdate ? calculateAge(profile.birthdate) : (profile.age ? parseInt(profile.age) : null),
        weight: profile.weight ? parseFloat(profile.weight) : null,
        weightUnit: profile.weightUnit,
        goalWeight: profile.goalWeight ? parseFloat(profile.goalWeight) : null,
        goalWeightUnit: profile.goalWeightUnit,
        // Height: store in both formats for compatibility
        height: profile.height ? parseFloat(profile.height) : null,
        heightUnit: profile.heightUnit || 'in',
        heightIn: profile.height ? (profile.heightUnit === 'cm' ? parseFloat(profile.height) * 0.393701 : parseFloat(profile.height)) : null,
        gender: profile.gender || null,
        experienceLevel: profile.experienceLevel || null,
        personaId: profile.personaId || null,
        appleHealthConnected: appleHealthConnected,
        appleHealthLastSync: appleHealthLastSync ? appleHealthLastSync : null,
        profilePictureUrl: profilePictureUrl,
      });
      
      // Update user state with new profile picture URL
      if (profilePictureUrl) {
        setUser(prev => ({ ...prev, profilePictureUrl }));
        setProfilePicturePreview(profilePictureUrl);
      }
      
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[Settings] Error saving:', error);
      setSuccessMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save effect - debounced (only after user makes changes)
  useEffect(() => {
    // Don't auto-save until user has actually made a change
    if (!hasUserMadeChanges.current) return;

    // Skip if already saving
    if (saving) return;

    // Skip if uploading picture (handle separately)
    if (uploadingPicture) return;

    // Debounce auto-save
    const timeoutId = setTimeout(async () => {
      setSaving(true);
      setSuccessMessage('');
      
      // Upload profile picture if changed
      let profilePictureUrl = user?.profilePictureUrl || null;
      if (profilePictureFile) {
        try {
          const fileExtension = profilePictureFile.name.split('.').pop();
          const storageRef = ref(storage, `users/${userId}/profile-picture.${fileExtension}`);
          await uploadBytes(storageRef, profilePictureFile);
          profilePictureUrl = await getDownloadURL(storageRef);
          setProfilePictureFile(null); // Clear after upload
        } catch (error) {
          console.error('[Settings] Error uploading profile picture:', error);
        }
      }
      
      // Convert pace inputs to storage format
      const runPaceSecondsPerMile = (paceSettings.runPaceMinutes * 60) + paceSettings.runPaceSeconds;
      const swimPacePer100 = paceSettings.swimPaceMinutes + (paceSettings.swimPaceSeconds / 60);
      
      try {
        await updateUserProfile(userId, {
          settings: {
            ...settings,
            themePreference: themePreference, // Include theme preference in settings
          },
          paceSettings: {
            runPaceSecondsPerMile: runPaceSecondsPerMile,
            runPaceDisplay: `${paceSettings.runPaceMinutes}:${String(paceSettings.runPaceSeconds).padStart(2, '0')}`,
            bikingAvgMph: paceSettings.bikingAvgMph,
            walkPaceMinutesPerMile: paceSettings.walkPaceMinutes,
            swimPacePer100: swimPacePer100,
            swimPaceUnit: 'yards',
          },
          birthdate: profile.birthdate || null,
          age: profile.birthdate ? calculateAge(profile.birthdate) : (profile.age ? parseInt(profile.age) : null),
          weight: profile.weight ? parseFloat(profile.weight) : null,
          weightUnit: profile.weightUnit,
          goalWeight: profile.goalWeight ? parseFloat(profile.goalWeight) : null,
          goalWeightUnit: profile.goalWeightUnit,
          // Height: store in both formats for compatibility
          height: profile.height ? parseFloat(profile.height) : null,
          heightUnit: profile.heightUnit || 'in',
          heightIn: profile.height ? (profile.heightUnit === 'cm' ? parseFloat(profile.height) * 0.393701 : parseFloat(profile.height)) : null,
          gender: profile.gender || null,
          experienceLevel: profile.experienceLevel || null,
          personaId: profile.personaId || null,
          appleHealthConnected: appleHealthConnected,
          appleHealthLastSync: appleHealthLastSync ? appleHealthLastSync : null,
          profilePictureUrl: profilePictureUrl,
        });
        
        // Update user state with new profile picture URL
        if (profilePictureUrl) {
          setUser(prev => ({ ...prev, profilePictureUrl }));
        }
        
        setSuccessMessage('Settings saved');
        setTimeout(() => setSuccessMessage(''), 2000);
      } catch (error) {
        console.error('[Settings] Error auto-saving:', error);
        setSuccessMessage('Failed to save');
        setTimeout(() => setSuccessMessage(''), 3000);
      } finally {
        setSaving(false);
      }
    }, 1000); // 1 second delay after change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, profile, paceSettings, appleHealthConnected, appleHealthLastSync, profilePictureFile, initialLoadComplete, themePreference]);

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        await signOut(auth);
        navigate('/');
      } catch (error) {
        console.error('[Settings] Error signing out:', error);
      }
    }
  };

  // DEV: Clear all activities and re-import program
  // eslint-disable-next-line no-unused-vars
  const handleResetAndReimport = async () => {
    if (!window.confirm('This will DELETE all scheduled activities and re-import your program. Continue?')) {
      return;
    }
    
    setDevAction('reimport');
    try {
      // Clear all scheduled activities
      console.log('[Settings DEV] Clearing all activities...');
      await clearAllScheduledActivities(userId);
      
      // Get the program to re-import
      const programId = user?.selectedProgramId || user?.personaId;
      if (programId) {
        console.log('[Settings DEV] Re-importing program:', programId);
        const result = await setupProgramByPersona(userId, programId);
        console.log('[Settings DEV] Import result:', result);
        setSuccessMessage(`Program re-imported! ${result.activitiesCreated || 0} activities created.`);
      } else {
        setSuccessMessage('Activities cleared. No program to re-import.');
      }
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('[Settings DEV] Error:', error);
      alert('Error: ' + error.message);
    } finally {
      setDevAction('');
    }
  };

  // DEV: Reset to onboarding
  // eslint-disable-next-line no-unused-vars
  const handleResetToOnboarding = async () => {
    if (!window.confirm('This will reset your profile and send you back to onboarding. All program data will be lost. Continue?')) {
      return;
    }
    
    setDevAction('onboarding');
    try {
      // Clear activities
      await clearAllScheduledActivities(userId);
      
      // Reset onboarding-related fields
      await updateUserProfile(userId, {
        onboardingComplete: false,
        personaId: null,
        selectedProgramId: null,
        programId: null,
        currentProgramId: null,
        currentMacrocycleId: null,
        liabilityWaiverAccepted: false,
      });
      
      // Navigate to onboarding
      navigate('/onboarding');
    } catch (error) {
      console.error('[Settings DEV] Error:', error);
      alert('Error: ' + error.message);
      setDevAction('');
    }
  };

  // Resize image if too large
  const resizeImage = (file, maxWidth = 1200, maxHeight = 1200) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions if image is too large
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, { type: file.type || 'image/jpeg' });
            resolve(resizedFile);
          }, file.type || 'image/jpeg', 0.9);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle profile picture upload
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Resize if image is too large
        const processedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          const imageData = reader.result;
          setOriginalImageData(imageData);
          setCropImage(imageData);
          setZoom(1);
          setImagePosition({ x: 0, y: 0 });
          setShowCropModal(true);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('[Settings] Error processing image:', error);
      }
    }
  };

  // Handle zoom
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.5, Math.min(prev + delta, 3)));
  };

  // Handle image drag
  const handleImageMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleImageMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setImagePosition({ x: newX, y: newY });
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  // Apply crop and create preview (square crop)
  const applyCrop = () => {
    if (!cropImage || !imageNaturalSize.width || !imageNaturalSize.height) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const outputSize = 400;
      
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      const containerSize = 400;
      const containerCenter = containerSize / 2;
      
      const imageAspect = img.width / img.height;
      
      let baseDisplayWidth, baseDisplayHeight;
      if (imageAspect > 1) {
        baseDisplayWidth = containerSize;
        baseDisplayHeight = containerSize / imageAspect;
      } else {
        baseDisplayWidth = containerSize * imageAspect;
        baseDisplayHeight = containerSize;
      }
      
      const currentImageCenterX = containerCenter + imagePosition.x;
      const currentImageCenterY = containerCenter + imagePosition.y;
      
      const offsetX = containerCenter - currentImageCenterX;
      const offsetY = containerCenter - currentImageCenterY;
      
      const pixelsPerContainerPixelX = img.width / (baseDisplayWidth * zoom);
      const pixelsPerContainerPixelY = img.height / (baseDisplayHeight * zoom);
      
      const sourceCenterX = (img.width / 2) + (offsetX * pixelsPerContainerPixelX);
      const sourceCenterY = (img.height / 2) + (offsetY * pixelsPerContainerPixelY);
      
      // Square crop - use the same size for both dimensions
      const avgPixelsPerContainerPixel = (pixelsPerContainerPixelX + pixelsPerContainerPixelY) / 2;
      const sourceCropSize = cropMaskSize * avgPixelsPerContainerPixel;
      const sourceCropRadius = sourceCropSize / 2;
      
      const sourceX = Math.max(0, Math.min(sourceCenterX - sourceCropRadius, img.width - sourceCropSize));
      const sourceY = Math.max(0, Math.min(sourceCenterY - sourceCropRadius, img.height - sourceCropSize));
      const finalSourceSize = Math.min(sourceCropSize, img.width - sourceX, img.height - sourceY);
      
      // Square crop - draw directly without clipping to circle
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        finalSourceSize,
        finalSourceSize,
        0,
        0,
        outputSize,
        outputSize
      );
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' });
        setProfilePictureFile(file);
        setProfilePicturePreview(canvas.toDataURL());
        setShowCropModal(false);
        setCropImage(null);
        setZoom(1);
        setImagePosition({ x: 0, y: 0 });
        hasUserMadeChanges.current = true;
      }, 'image/jpeg', 0.9);
    };
    img.src = cropImage;
  };

  // Cancel crop
  const cancelCrop = () => {
    setShowCropModal(false);
    setCropImage(null);
    setZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Handle crop button click - use original image if available
  const handleCropClick = () => {
    const imageToCrop = originalImageData || profilePicturePreview;
    if (imageToCrop) {
      setCropImage(imageToCrop);
      setZoom(1);
      setImagePosition({ x: 0, y: 0 });
      setShowCropModal(true);
    }
  };


  // Don't show loading screen - render layout immediately
  return (
    <>
    <div className="dashboard-page has-bottom-nav">
      {/* Background effects */}
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      {/* Sidebar */}
      <DashboardSidebar userId={userId} user={user} />

      {/* Main content */}
      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            <h1>Settings</h1>
          </div>
        </div>

        {/* Content */}
        <div className="settings-content">
          {successMessage && (
            <div className="settings-success-message">{successMessage}</div>
          )}

          {/* Profile Card */}
          <div className="dashboard-card settings-profile-card">
            <div className="settings-profile-card-content">
              <div className="settings-profile-picture-container">
                {profilePicturePreview || user?.profilePictureUrl ? (
                  <img 
                    src={profilePicturePreview || user?.profilePictureUrl || Badge} 
                    alt="Profile" 
                    className="settings-profile-picture"
                  />
                ) : (
                  <div className="settings-profile-picture-placeholder">
                    <img src={Badge} alt="Profile" />
                  </div>
                )}
              </div>
              <div className="settings-profile-info">
                <h2 className="settings-profile-name">
                  {user?.displayName || (user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.email || 'User')}
                </h2>
                <p className="settings-profile-email">{user?.email || ''}</p>
              </div>
              <button
                type="button"
                className="c-ripple settings-profile-edit-btn"
                onClick={() => setShowProfileEdit(true)}
                aria-label="Edit profile"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* DEV: Testing Tools - Hidden for now, uncomment to enable */}
          {/* {user?.personaId === 'athlete' && user?.selectedProgramId === 'court_volleyball' && (
            <div className="dashboard-card settings-section" style={{ borderLeft: '3px solid #FF0000' }}>
              <h2 className="settings-section-title" style={{ color: '#FF0000' }}>🛠️ Developer Tools</h2>
              <p className="settings-section-description">
                Testing tools for resetting and re-importing program data.
              </p>
              
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                <button
                  onClick={handleResetAndReimport}
                  disabled={devAction !== ''}
                  className="c-ripple"
                  style={{
                    background: '#353531',
                    color: '#FFF',
                    border: '1px solid rgba(255,255,255,0.2)',
                    padding: '12px 20px',
                    cursor: devAction ? 'wait' : 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                  }}
                >
                  {devAction === 'reimport' ? 'Re-importing...' : 'Clear & Re-import Program'}
                </button>
                
                <button
                  onClick={handleResetToOnboarding}
                  disabled={devAction !== ''}
                  className="c-ripple"
                  style={{
                    background: 'rgba(255,0,0,0.2)',
                    color: '#FF6666',
                    border: '1px solid #FF0000',
                    padding: '12px 20px',
                    cursor: devAction ? 'wait' : 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                  }}
                >
                  {devAction === 'onboarding' ? 'Resetting...' : '⚠️ Reset & Go to Onboarding'}
                </button>
              </div>
            </div>
          )} */}

          {/* CLUB SECTION - for club owners */}
          <ClubSettingsSection userId={userId} ownedClubIds={user?.ownedClubIds} />

          {/* COACH SECTION - for coaches */}
          <CoachSettingsSection userId={userId} coachId={user?.coachId} />

          {/* ACCOUNT SECTION */}
          <div id="settings-account-section" className="settings-section-wrapper">
            <h3 className="settings-section-header">ACCOUNT</h3>
            <div className="dashboard-card settings-section-card">
              <div className="settings-row" onClick={() => setShowProfileEdit(true)}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Edit Profile</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row" onClick={() => navigate(`/${userId}/change-program`)}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Change Program</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Notifications</span>
                  </div>
                </div>
                <label className="settings-toggle" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={settings.notifications}
                    onChange={(e) => handleSettingsChange('notifications', e.target.checked)}
                  />
                  <span className="settings-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* PREFERENCES SECTION */}
          <div className="settings-section-wrapper">
            <h3 className="settings-section-header">PREFERENCES</h3>
            <div className="dashboard-card settings-section-card">
              <div className="settings-row" onClick={() => handleSettingsChange('units', settings.units === 'imperial' ? 'metric' : 'imperial')}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Units</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-value">
                    {settings.units === 'imperial' ? 'Imperial' : 'Metric'}
                  </span>
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row" onClick={() => setShowPaceSettings(true)}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Pace</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    {themePreference === 'light' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                    )}
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Theme</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-value">
                    {themePreference === 'system' ? 'System' : themePreference === 'light' ? 'Light' : 'Dark'}
                  </span>
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row">
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 6.5V17.5"/><path d="M17.5 6.5V17.5"/><path d="M6.5 12H17.5"/><path d="M3 8V16"/><path d="M21 8V16"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Equipment</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-value">Begin Gym</span>
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
            </div>
          </div>

          {/* SUPPORT SECTION */}
          <div className="settings-section-wrapper">
            <h3 className="settings-section-header">SUPPORT</h3>
            <div className="dashboard-card settings-section-card">
              <div className="settings-row" onClick={() => navigate('/faqs')}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">FAQ</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row" onClick={() => navigate('/terms')}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Terms of Service</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
              <div className="settings-row" onClick={() => navigate('/privacy')}>
                <div className="settings-row-left">
                  <span className="settings-row-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label">Privacy Policy</span>
                  </div>
                </div>
                <span className="settings-row-right">
                  <span className="settings-row-chevron"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                </span>
              </div>
            </div>
          </div>

          {/* Profile Edit Screen - Slide-in from right */}
          <ProfileEditScreen 
            isOpen={showProfileEdit} 
            onClose={() => setShowProfileEdit(false)}
          >
            <div className="settings-section-wrapper">
              <div className="dashboard-card settings-section-card">
              {/* Profile Picture */}
              <div className="settings-form-item">
                <label className="settings-form-label">Profile Picture</label>
                <div className="settings-form-control" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  {profilePicturePreview || user?.profilePictureUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, background: '#353531', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img 
                        src={profilePicturePreview || user?.profilePictureUrl || Badge} 
                        alt="Profile" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      <button
                        type="button"
                        className="c-ripple settings-crop-button"
                        onClick={handleCropClick}
                        style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #FF0000', color: '#FF0000', borderRadius: '0', fontSize: '14px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}
                      >
                        Crop
                      </button>
                      <label style={{ cursor: 'pointer' }}>
                        <input
                          type="file"
                          accept="image/*,.webp,image/webp"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                        <span className="c-ripple settings-change-link" style={{ display: 'inline-block', padding: '8px 16px', background: '#FF0000', color: 'white', borderRadius: '0', fontSize: '14px', fontFamily: 'Poppins, sans-serif', textDecoration: 'none', cursor: 'pointer' }}>
                          Replace
                        </span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <label style={{ cursor: 'pointer' }}>
                    <input
                      type="file"
                      accept="image/*,.webp,image/webp"
                      onChange={handleProfilePictureChange}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#FF0000', color: 'white', borderRadius: '0', fontSize: '14px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
                      <img src={UploadIcon} alt="Upload" style={{ width: '16px', height: '16px' }} />
                      <span>Upload Picture</span>
                    </div>
                  </label>
                )}
                </div>
              </div>

            {/* Crop Modal */}
            {showCropModal && cropImage && (
              <div className="settings-crop-modal" onClick={cancelCrop}>
                <div className="settings-crop-modal-content" onClick={(e) => e.stopPropagation()}>
                  <h3 className="settings-crop-modal-title">Crop Profile Picture</h3>
                  <div 
                    className="settings-crop-container"
                    onWheel={handleWheel}
                  >
                    <div 
                      className="settings-crop-image-container"
                      onMouseDown={handleImageMouseDown}
                      onMouseMove={handleImageMouseMove}
                      onMouseUp={handleImageMouseUp}
                      onMouseLeave={handleImageMouseUp}
                      style={{
                        cursor: isDragging ? 'grabbing' : 'grab'
                      }}
                    >
                      <img 
                        src={cropImage} 
                        alt="Crop" 
                        className="settings-crop-image"
                        onLoad={(e) => {
                          const img = e.target;
                          setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                        }}
                        style={{
                          transform: `translate(-50%, -50%) translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoom})`,
                          transformOrigin: 'center center'
                        }}
                      />
                    </div>
                    {/* Square crop mask overlay */}
                    <div className="settings-crop-overlay" />
                  </div>
                  {/* Zoom controls */}
                  <div className="settings-crop-zoom-controls">
                    <button
                      type="button"
                      className="settings-crop-zoom-button"
                      onClick={handleZoomOut}
                    >
                      −
                    </button>
                    <span className="settings-crop-zoom-value">{Math.round(zoom * 100)}%</span>
                    <button
                      type="button"
                      className="settings-crop-zoom-button"
                      onClick={handleZoomIn}
                    >
                      +
                    </button>
                  </div>
                  <div className="settings-crop-actions">
                    <button
                      type="button"
                      className="settings-crop-cancel"
                      onClick={cancelCrop}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="settings-crop-apply"
                      onClick={applyCrop}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="settings-form-item">
              <label className="settings-form-label">Age</label>
              <div className="settings-form-control">
                {profile.birthdate ? (
                  <span className="settings-value">{calculateAge(profile.birthdate)} years</span>
                ) : (
                  <>
                    <input
                      type="number"
                      value={profile.age}
                      onChange={(e) => handleProfileChange('age', e.target.value)}
                      className="settings-input"
                      min="1"
                      max="120"
                    />
                    <span className="settings-description">years</span>
                  </>
                )}
              </div>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">Birthdate</label>
              <div className="settings-form-control">
                <input
                  type="date"
                  value={profile.birthdate}
                  onChange={(e) => handleProfileChange('birthdate', e.target.value)}
                  className="settings-input"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">Gender</label>
              <div className="settings-form-control">
                <select
                  value={profile.gender}
                  onChange={(e) => handleProfileChange('gender', e.target.value)}
                  className="settings-select"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">Current Weight</label>
              <div className="settings-form-control">
                <input
                  type="number"
                  value={profile.weight}
                  onChange={(e) => handleProfileChange('weight', e.target.value)}
                  className="settings-input"
                  min="0"
                  step="0.1"
                />
                <select
                  value={profile.weightUnit}
                  onChange={(e) => handleProfileChange('weightUnit', e.target.value)}
                  className="settings-select"
                  style={{ minWidth: '80px', marginLeft: '8px' }}
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </div>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">Height</label>
              <div className="settings-form-control">
                <input
                  type="number"
                  value={profile.height}
                  onChange={(e) => handleProfileChange('height', e.target.value)}
                  className="settings-input"
                  min="0"
                  step="0.1"
                />
                <select
                  value={profile.heightUnit}
                  onChange={(e) => handleProfileChange('heightUnit', e.target.value)}
                  className="settings-select"
                  style={{ minWidth: '80px', marginLeft: '8px' }}
                >
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                </select>
              </div>
            </div>

            <div className="settings-form-item">
              <label className="settings-form-label">Goal Weight</label>
              <div className="settings-form-control">
                <input
                  type="number"
                  value={profile.goalWeight}
                  onChange={(e) => handleProfileChange('goalWeight', e.target.value)}
                  className="settings-input"
                  min="0"
                  step="0.1"
                />
                <select
                  value={profile.goalWeightUnit}
                  onChange={(e) => handleProfileChange('goalWeightUnit', e.target.value)}
                  className="settings-select"
                  style={{ minWidth: '80px', marginLeft: '8px' }}
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </div>
            </div>

              </div>
            </div>

            {/* Delete Account - inside Edit Profile */}
            <div className="settings-section-wrapper" style={{ marginTop: '32px' }}>
              <div
                className="settings-row"
                onClick={() => setShowDeleteAccountModal(true)}
                style={{
                  background: 'var(--color-bg-card-solid)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <div className="settings-row-left">
                  <span className="settings-row-icon" style={{ color: 'var(--color-error)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </span>
                  <div className="settings-row-content">
                    <span className="settings-row-label" style={{ color: 'var(--color-error)' }}>Delete Data and Account</span>
                  </div>
                </div>
              </div>
              <p style={{
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginTop: '8px',
                paddingLeft: '4px',
                fontFamily: 'var(--font-family)',
              }}>
                This will permanently delete your account and all data.
              </p>
            </div>
          </ProfileEditScreen>

          {/* SIGN OUT BUTTON */}
          <div className="settings-section-wrapper">
            <button
              type="button"
              className="c-ripple settings-signout-button"
              onClick={handleSignOut}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Sign Out
            </button>
          </div>

          {/* VERSION LABEL */}
          <div className="settings-version-label">
            Begin Strength v0.1.0
          </div>

        </div>
      </div>
    </div>

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="settings-modal-overlay" onClick={() => !isDeleting && setShowDeleteAccountModal(false)}>
          <div className="settings-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="settings-modal-header">
              <h2 className="settings-modal-title" style={{ color: '#FF0000' }}>Delete Account</h2>
              {!isDeleting && (
                <button
                  className="c-ripple settings-modal-close"
                  onClick={() => setShowDeleteAccountModal(false)}
                >
                  ×
                </button>
              )}
            </div>
            <div className="settings-modal-body">
              {!showDeleteConfirmation ? (
                <>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: '16px',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    This will permanently delete your account and all your data. This action <strong>cannot be undone</strong>.
                  </p>
                  <div style={{
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '1px solid #FF0000',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      fontSize: '14px',
                      lineHeight: '1.6',
                      marginBottom: '12px',
                      fontWeight: '600'
                    }}>
                      All of the following will be deleted:
                    </p>
                    <ul style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px',
                      lineHeight: '1.8',
                      paddingLeft: '20px',
                      margin: 0
                    }}>
                      <li>Your profile and personal information</li>
                      <li>All workout history and progress</li>
                      <li>All scheduled activities</li>
                      <li>Your training program data</li>
                      <li>All performance tracking data</li>
                    </ul>
                  </div>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}>
                    Are you absolutely sure?
                  </p>
                </>
              ) : (
                <>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: '16px',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    To confirm deletion, please type <strong style={{ color: '#FF0000' }}>DELETE</strong> in the box below:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="settings-input"
                    style={{ width: '100%', marginBottom: '16px' }}
                    disabled={isDeleting}
                    autoFocus
                  />
                  {isDeleting && (
                    <p style={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      fontSize: '14px',
                      textAlign: 'center',
                      marginTop: '16px'
                    }}>
                      Deleting your account... This may take a moment.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="settings-modal-footer">
              {!showDeleteConfirmation ? (
                <>
                  <button
                    className="c-ripple settings-modal-cancel"
                    onClick={() => setShowDeleteAccountModal(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="c-ripple settings-modal-save"
                    onClick={() => setShowDeleteConfirmation(true)}
                    style={{ background: '#FF0000' }}
                    disabled={isDeleting}
                  >
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="c-ripple settings-modal-cancel"
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteConfirmText('');
                    }}
                    disabled={isDeleting}
                  >
                    Back
                  </button>
                  <button
                    className="c-ripple settings-modal-save"
                    onClick={async () => {
                      if (deleteConfirmText !== 'DELETE') {
                        alert('Please type DELETE exactly to confirm');
                        return;
                      }
                      
                      setIsDeleting(true);
                      try {
                        // 1. Delete all Firestore data
                        await deleteUserAccount(userId);
                        
                        // 2. Delete Firebase Auth account
                        const currentUser = auth.currentUser;
                        if (currentUser && currentUser.uid === userId) {
                          await deleteUser(currentUser);
                        }
                        
                        // 3. Sign out (in case deleteUser didn't do it)
                        await signOut(auth);
                        
                        // 4. Navigate to sign in
                        navigate('/signin');
                      } catch (error) {
                        console.error('[Settings] Error deleting account:', error);
                        alert('Error deleting account. Please try again or contact support.');
                        setIsDeleting(false);
                      }
                    }}
                    style={{ background: '#FF0000' }}
                    disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Pace Settings Modal */}
      <PaceSettingsModal
        isOpen={showPaceSettings}
        onClose={() => setShowPaceSettings(false)}
        paceSettings={paceSettings}
        onSave={handleSavePaceSettings}
      />

    {/* Bottom Navigation - Mobile/Tablet */}
    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Settings;

